import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import { useGetWithdrawHistoryQuery } from "../../../services/apiSlice";
import TransactionItem from "./TransactionItem";
import Breadcrumb from "../ProfileCommon/Breadcrumb";
import FooterContact from "../../../components/Footer/FooterContact";

const HistoryWithdraw = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const observerRef = useRef(null);
  const loadingRef = useRef(null);

  // State cho phân trang
  const [page, setPage] = useState(1);
  const [transactions, setTransactions] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 10;

  const {
    data: withdrawHistory,
    isLoading,
    isFetching,
    error,
  } = useGetWithdrawHistoryQuery({
    page: page,
    limit: PAGE_SIZE,
    order: "DESC",
  });

  useEffect(() => {
    setPage(1);
    setTransactions([]);
    setHasMore(true);
  }, []);

  useEffect(() => {
    if (withdrawHistory?.data) {
      if (page === 1) {
        setTransactions(withdrawHistory.data);
      } else {
        setTransactions((prev) => [...prev, ...withdrawHistory.data]);
      }
      setHasMore(withdrawHistory.data.length >= PAGE_SIZE);
    }
  }, [withdrawHistory, page]);

  const lastTransactionElementRef = useCallback(
    (node) => {
      if (isLoading || isFetching) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            setPage((prevPage) => prevPage + 1);
          }
        },
        { rootMargin: "100px" }
      );

      if (node) observerRef.current.observe(node);
    },
    [isLoading, isFetching, hasMore]
  );

  const uniqueTransactions = useMemo(() => {
    const seen = new Set();
    return transactions.filter((item) => {
      if (seen.has(item.withdraw_id)) return false;
      seen.add(item.withdraw_id);
      return true;
    });
  }, [transactions]);


  const renderMainContent = () => {
    if (isLoading && page === 1) {
      return <div className="flex justify-center items-center h-full">{t("base.loading")}</div>;
    }

    if (error && page === 1) {
      return (
        <div className="flex justify-center items-center h-full">{t("base.errorLoadingData")}</div>
      );
    }

    return (
      <div className="flex flex-col flex-1 px-[24px] pb-[120px]">
        <Breadcrumb
          className="mb-6"
          breadCrumbTiltle={t("historyWithdraw.withdrawHistory")}
          onClickBack={() =>
            navigate("/profile", {
              state: { direction: -1 },
              replace: false,
            })
          }
        />

        {uniqueTransactions.length === 0 && !isLoading && !isFetching ? (
          <div className="text-center text-gray-500 py-4">
            <p>{t("historyWithdraw.noWithdrawHistory")}</p>
          </div>
        ) : (
          <>
            {uniqueTransactions.map((item, index) => {
              const isLastElement = uniqueTransactions.length === index + 1;
              const uniqueKey = `${item.withdraw_id}-${index}`;
              return (
                <div key={uniqueKey} className="mb-4">
                  <TransactionItem
                    ref={isLastElement ? lastTransactionElementRef : null}
                    status={
                      item.status === "APPROVED"
                        ? "completed"
                        : item.status === "REJECTED"
                        ? "unsuccessful"
                        : "pending"
                    }
                    createdAt={item.created_at}
                    transactionsFee={item.transaction_fee}
                    receiveAmount={item.receive_amount}
                    tokenAmount={item.token_amount}
                    bankAccount={item.bank_account}
                  />
                </div>
              );
            })}
            {(isFetching || (isLoading && page > 1)) && (
              <div ref={loadingRef} className="flex justify-center items-center py-4 w-full">
                <div className="w-6 h-6 border-2 border-[#253FF6] border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-2 text-sm text-gray-600">{t("base.loading")}</span>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full max-w-[480px] h-full bg-white overflow-y-auto">
      <LoadingCommon statusLoading={isFetching || (isLoading && page === 1)} />
      {renderMainContent()}
      <FooterContact className={"!bg-white"} />
    </div>
  );
};

export default HistoryWithdraw;
